import Image from 'next/image';
import NextLink from 'next/link';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  CardProps,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  ResponsiveValue,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';

import { CircleArrowRightIcon, StarOutlineIcon } from '../icons';

export type PromoCardProps = {
  /** Unique identifier for the card's main heading */
  id: string;
  /** Header styling for the default eyebrow. Not needed when `eyebrowSlot` is supplied. */
  header?: {
    color: string;
    lineColor: string;
    lineGradient?: string;
  };
  /** Slot for background image - allows complete control over positioning */
  backgroundImageSlot?: React.ReactNode;
  /** Slot for content container - allows custom margins, etc. */
  contentSlot?: React.ReactNode;
  /** Slot for button - allows complete customization of CTA */
  buttonSlot?: React.ReactNode;
  /**
   * Replaces the default star + "New at Udacity" eyebrow row entirely (e.g. the catalog/flash bordered
   * pill eyebrow). When omitted, the original star eyebrow + full-width rule renders unchanged.
   */
  eyebrowSlot?: React.ReactNode;
  /** Vertical gap between eyebrow, content, and CTA. */
  sectionGap?: ResponsiveValue<number | string>;
  /** When set, the full card is clickable via LinkBox + LinkOverlay. */
  linkOverlay?: {
    ariaLabel: string;
    href: string;
    onClick?: () => void;
  };
} & CardProps;

export function PromoCard({
  backgroundImageSlot,
  buttonSlot,
  contentSlot,
  eyebrowSlot,
  header,
  id,
  linkOverlay,
  sectionGap = { base: 6, md: 8 },
  ...cardProps
}: PromoCardProps) {
  const { t } = useTranslation();
  const lineProps = header?.lineGradient ? { bgGradient: header.lineGradient } : { backgroundColor: header?.lineColor };

  const cardBody = (
    <>
      {backgroundImageSlot}

      <Flex
        flex={1}
        flexDirection='column'
        gap={sectionGap}
        pointerEvents={linkOverlay ? 'none' : undefined}
        position='relative'
        zIndex={linkOverlay ? 1 : undefined}
      >
        {eyebrowSlot ?? (
          <Flex
            alignItems='center'
            color={header?.color}
            gap={4}
            _after={{
              content: '""',
              display: 'block',
              width: '100%',
              height: '0.5px',
              ...lineProps,
            }}
          >
            <Flex alignItems='center' flex={1} gap={2}>
              <StarOutlineIcon height='16px' width='16px' />
              <Heading
                as='p'
                fontFamily='condensed'
                fontSize='0.9375rem'
                fontWeight='500'
                lineHeight='1'
                textTransform='uppercase'
                whiteSpace='nowrap'
              >
                {t('home.impact.newAtUdacity')}
              </Heading>
            </Flex>
          </Flex>
        )}

        {contentSlot}

        <Box mt='auto'>{buttonSlot}</Box>
      </Flex>

      {linkOverlay ? (
        <LinkOverlay
          aria-label={linkOverlay.ariaLabel}
          as={NextLink}
          href={linkOverlay.href}
          inset={0}
          position='absolute'
          zIndex={2}
          onClick={linkOverlay.onClick}
        />
      ) : null}
    </>
  );

  if (linkOverlay) {
    return (
      <LinkBox aria-labelledby={id} as='aside' width='100%'>
        <Card as='div' boxShadow='none' isolation='isolate' overflow='hidden' position='relative' {...cardProps}>
          {cardBody}
        </Card>
      </LinkBox>
    );
  }

  return (
    <Card aria-labelledby={id} as='aside' boxShadow='none' {...cardProps} overflow='hidden' position='relative'>
      {cardBody}
    </Card>
  );
}

export function MbaAiPromoCard({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <PromoCard
      backgroundColor='#0A0B0F'
      bgGradient={{ base: 'linear(180deg, #2B050C 0%, #0A0B0F 100%)', md: 'none' }}
      id='mba-ai-title'
      backgroundImageSlot={
        <>
          <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
            <Image
              alt=''
              src='/images/experiments/ab_plp_redesign_2025/mba-promo-card-bg-mobile.png'
              style={{ objectFit: 'cover', objectPosition: 'right top' }}
              fill
            />
          </Box>
          <Box bottom={0} display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
            <Image
              alt=''
              src='/images/experiments/ab_plp_redesign_2025/mba-promo-card-bg.png'
              style={{ objectFit: 'cover', objectPosition: 'right center' }}
              fill
            />
          </Box>
        </>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: '/mba-ai-product-management' }}
          buttonProps={{
            size: 'icon-left',
            leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
            variant: 'solid',
            colorScheme: 'blue',
            position: 'relative',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {t('common.learnMore')}
          <VisuallyHidden>{t('home.impact.mbaAi.learnMoreAria')}</VisuallyHidden>
        </ButtonLink>
      }
      contentSlot={
        <Box
          marginRight={{
            base: '102px',
            md: 'unset',
          }}
        >
          <Heading
            as='h2'
            color='white'
            fontWeight='600'
            id='mba-ai-title'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.1',
            }}
            whiteSpace={{
              base: 'pre-wrap',
              md: 'normal',
            }}
          >
            <LinkOverlay href='/mba-ai-product-management' onClick={onClick}>
              {t('home.impact.mbaAi.title')}
            </LinkOverlay>
          </Heading>
          <Text color='grey.300'>{t('home.impact.mbaAi.description')}</Text>
        </Box>
      }
      header={{
        color: '#EDA93B',
        lineColor: '#915B14',
        lineGradient: 'linear(90deg, #915B14 0%, rgba(145, 91, 20, 0) 100%)',
      }}
      padding={{
        base: '32px',
        md: '48px 262px 56px 48px',
      }}
    />
  );
}

export function MastersAIPromoCard({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <PromoCard
      bgGradient='linear(90deg, #071C4E 0%, #060A12 100%)'
      id='masters-ai-title'
      backgroundImageSlot={
        <Box
          bottom={0}
          position='absolute'
          right={{
            base: '-64px',
            md: '0px',
          }}
          top={{
            base: '-38px',
            md: '0px',
          }}
          width={{
            base: '324px',
            md: '432px',
          }}
        >
          <Image
            alt=''
            quality={100}
            src='/images/experiments/ab_plp_redesign_2025/woolf-masters-seal-bg.png'
            style={{
              objectFit: 'contain',
              objectPosition: 'center',
            }}
            fill
          />
        </Box>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: '/masters-artificial-intelligence' }}
          buttonProps={{
            size: 'icon-left',
            leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
            variant: 'solid',
            colorScheme: 'blue',
            position: 'relative',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {t('common.learnMore')}
          <VisuallyHidden>{t('home.impact.mastersAi.learnMoreAria')}</VisuallyHidden>
        </ButtonLink>
      }
      contentSlot={
        <Box
          marginRight={{
            base: '102px',
            md: 'unset',
          }}
        >
          <Heading
            as='h2'
            color='white'
            fontWeight='600'
            id='masters-ai-title'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.14',
              md: '1.1',
            }}
          >
            <LinkOverlay href='/masters-artificial-intelligence' onClick={onClick}>
              {t('home.impact.mastersAi.title')}
            </LinkOverlay>
          </Heading>
          <Text color='grey.300'>{t('home.impact.mastersAi.description')}</Text>
        </Box>
      }
      header={{
        color: '#EDA93B',
        lineColor: '#915B14',
        lineGradient: 'linear(90deg, #915B14 0%, rgba(145, 91, 20, 0) 100%)',
      }}
      padding={{
        base: '32px',
        md: '48px 262px 56px 48px',
      }}
    />
  );
}
